import {ReactElement, useMemo, useState} from "react";
import {IUnitType, IUseRevenueWizardState, TMetricFavorable} from "../logic/useRevenueWizard";
import * as css from "../styles/unit-summary-card.module.scss";
import {Help} from "@material-ui/icons";
import {round} from "../../../utils/math";
import {IconButton} from "@zendeskgarden/react-buttons";
import {VersionType} from "../../../__generated__/generated_types";


interface IUnitTypeSummaryCardProps {
    unitType: IUnitType;
    isSummaryUnit: boolean;
    metricToFavor: TMetricFavorable;
    revenueWizardInstance: IUseRevenueWizardState;
    previewingVersionType: VersionType;
}

export default function UnitTypeSummaryCard({unitType, isSummaryUnit, metricToFavor, revenueWizardInstance, previewingVersionType}: IUnitTypeSummaryCardProps): ReactElement {
    // point delta to determine if we consider a calculated value close enough to meet a user's target goal
    const targetToleranceDelta = 2;

    const {
        property,
        budgetYear,
        countToRate,
        rateToCount,
        renewalRate,
        occupancyRate,
        forceRounding,
        getPreviousOccupancy,
        selectedMetricToFavor,
    } = revenueWizardInstance;

    const [infoVisible, setInfoVisible] = useState(false);

    const derivedMetrics = useMemo(() => {
        const utv = previewingVersionType === VersionType.Reforecast ? unitType.rfcst : unitType.bdgt;

        const expirationCount = utv.expirationCount.sum();
        const moveInCount = utv.moveInCount.sum();
        const unitCountSeasonTotal = unitType.unitCount * 12;

        return {
            expirations: expirationCount,
            moveIns: moveInCount,
            correctedRenewalRate: countToRate(utv.renewalCount.sum(), expirationCount),
            correctedOccupancyRate: countToRate(utv.occupancyCount.sum(), unitCountSeasonTotal),
            previousOccupancy: getPreviousOccupancy(unitType, VersionType.Reforecast, property.reforecastStartMonthIndex),
        };
    }, [unitType, previewingVersionType]);

    function metricColumn(label: string, rate: number, _count: number, comparisonRate: number, caresAboutExpirations = false) {
        const onTargetClass = Math.abs(rate - comparisonRate) < targetToleranceDelta ? css.onTarget : "";

        return (
            <div className={css.metricColumn}>
                <h5 className={css.label}>{label}</h5>
                <span className={`${css.rate} ${onTargetClass}`}>
                    {derivedMetrics.expirations <= 0 && caresAboutExpirations ?
                        "N/A"
                        :
                        `${round(rate, 1)}%`
                    }
                </span>
            </div>
        );
    }

    // I wish we had a better way to cover the explanations, and give more detailed answers for every possible
    // scenario, but for now these are the ones we have to start, and it can be updated or improved upon later
    function getInfoText() {
        const occupancyOnTarget = Math.abs(derivedMetrics.correctedOccupancyRate - occupancyRate) < targetToleranceDelta;
        const renewalRateOnTarget = Math.abs(derivedMetrics.correctedRenewalRate - renewalRate) < targetToleranceDelta;
        const previousOccupancyRate = round(countToRate(derivedMetrics.previousOccupancy, unitType.unitCount), 1);
        const displayOccupancyRate = round(derivedMetrics.correctedOccupancyRate, 1);

        if (unitType.unitCount == 0) {
            return "Unit Type is registered in Vizibly, but count is 0 so nothing to calculate.";
        }

        if (occupancyOnTarget && (renewalRateOnTarget || derivedMetrics.expirations <= 0)) {
            return "Looks like we can hit your targets!";
        }

        if (isSummaryUnit) {
            return "View detailed Unit Type breakdown for more information.";
        }

        if ((!occupancyOnTarget || !renewalRateOnTarget) && forceRounding) {
            return `Rounding snaps our rates to only give values that result in whole unit types.`;
        }

        if (metricToFavor == "occupancy") {
            if (occupancyOnTarget && !renewalRateOnTarget) {
                return `Not enough Expirations to move your Renewal Rate past ${derivedMetrics.correctedRenewalRate}% with this Occupancy.`;
            }
        }

        if (metricToFavor == "renewals") {
            if (derivedMetrics.correctedRenewalRate == 100 && !occupancyOnTarget) {
                return `A 100% Renewal Rate means our Occupancy cannot go below starting Actuals Occupancy rate of ${previousOccupancyRate}%.`;
            }

            if (!occupancyOnTarget && renewalRateOnTarget && previewingVersionType == VersionType.Budget) {
                return `Not enough Expirations to create Move Outs needed to reach an Occupancy beyond ${displayOccupancyRate}%.`;
            }
        }

        if (derivedMetrics.expirations <= 0 && !occupancyOnTarget) {
            return `Our starting Actuals Occupancy was ${previousOccupancyRate}%, and with no Expirations we can't go below that.`;
        }

        if ((!occupancyOnTarget || !renewalRateOnTarget) && previewingVersionType == VersionType.Reforecast) {
            return `The ${budgetYear - 1} Actuals impact the Reforecast average. Check Summary Table on next step for more details.`;
        }

        // if we see this in the UI, it means we have a scenario we didn't think of and need to create a message for it
        return "This is the closest we could get to your targets. Check Summary Table on next step for more details.";
    }

    return (
        <div className={`${css.unitTypeSummaryCard} ${isSummaryUnit && css.isSummary} ${selectedMetricToFavor == metricToFavor && css.isSelectedMetricToFavor} ${infoVisible && css.infoVisible}`}>
            <div className={css.header}>
                <h4 className={css.title}>{unitType.name} - {unitType.unitCount} Units</h4>
                <IconButton
                    size="small"
                    className={css.infoTrigger} 
                    onClick={() => setInfoVisible(!infoVisible)}
                    aria-expanded={infoVisible}
                    aria-label="toggle info about unit type numbers"
                ><Help /></IconButton>
            </div>

            <div className={css.metricRow}>
                <div className={css.metricColumn}>
                    <h5 className={css.label}>Expirations</h5>
                    <span className={css.rate}>{derivedMetrics.expirations}</span>
                </div>
                {metricColumn(
                    "Renewals",
                    derivedMetrics.correctedRenewalRate,
                    rateToCount(derivedMetrics.correctedRenewalRate, derivedMetrics.expirations),
                    renewalRate,
                    true,
                )}
                {metricColumn(
                    "Occupancy",
                    derivedMetrics.correctedOccupancyRate,
                    rateToCount(derivedMetrics.correctedOccupancyRate, unitType.unitCount),
                    occupancyRate,
                )}
                <div className={css.metricColumn}>
                    <h5 className={css.label}>Move Ins</h5>
                    <span className={css.rate}>{round(derivedMetrics.moveIns, 1)}</span>
                </div>
            </div>
            <div
                className={css.infoBackdrop}
                onClick={() => {
                    if (infoVisible) {
                        setInfoVisible(false);
                    }
                }}
            ></div>
            <div className={css.infoPanel} aria-hidden={!infoVisible}>
                <p>{getInfoText()}</p>
            </div>
        </div>
    );
}
